import React, { useState, useEffect, useRef } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend } from 'chart.js';
import { Line, Bar, Pie } from 'react-chartjs-2';
import { BarChart3, ShoppingBag, DollarSign, TrendingUp, Gift, Star, Wallet, Download, Calendar, Package, Eye, Store } from 'lucide-react';
import './CustomerAnalytics.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend);

const CustomerAnalytics = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [period, setPeriod] = useState('month');
  const [customDates, setCustomDates] = useState({ startDate: '', endDate: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [orderHistory, setOrderHistory] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch analytics overview
  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ period });

      if (period === 'custom' && customDates.startDate && customDates.endDate) {
        params.append('startDate', customDates.startDate);
        params.append('endDate', customDates.endDate);
      }

      const response = await fetch(`/api/customer/analytics/overview?${params}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch analytics data');
      }

      const result = await response.json();
      setAnalyticsData(result.data);
      setError(null);
    } catch (err) {
      console.error('Analytics fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch order history
  const fetchOrderHistory = async (page = 1) => {
    try {
      const params = new URLSearchParams({ period, page, limit: 10 });

      if (period === 'custom' && customDates.startDate && customDates.endDate) {
        params.append('startDate', customDates.startDate);
        params.append('endDate', customDates.endDate);
      }

      const response = await fetch(`/api/customer/analytics/orders?${params}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch order history');
      }

      const result = await response.json();
      setOrderHistory(result.data);
    } catch (err) {
      console.error('Order history fetch error:', err);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    if (activeTab === 'orders') {
      fetchOrderHistory(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, customDates, activeTab, currentPage]);

  // Export data
  const handleExport = async (format = 'csv') => {
    try {
      const params = new URLSearchParams({ period, format });

      if (period === 'custom' && customDates.startDate && customDates.endDate) {
        params.append('startDate', customDates.startDate);
        params.append('endDate', customDates.endDate);
      }

      const response = await fetch(`/api/customer/analytics/export?${params}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to export data');
      }

      if (format === 'csv') {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `customer-analytics-${period}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const data = await response.json();
        const jsonStr = JSON.stringify(data, null, 2);
        const blob = new Blob([jsonStr], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `customer-analytics-${period}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (err) {
      console.error('Export error:', err);
      alert('Failed to export data');
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    const numAmount = parseFloat(amount) || 0;
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(numAmount);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading && !analyticsData) {
    return (
      <div className="analytics-loading">
        <div className="spinner"></div>
        <p>Loading your analytics...</p>
      </div>
    );
  }

  if (error && !analyticsData) {
    return (
      <div className="analytics-error">
        <p>Error: {error}</p>
        <button onClick={fetchAnalytics}>Retry</button>
      </div>
    );
  }

  if (!analyticsData) {
    return null;
  }

  const { customer, summary, topCategories, topMerchants, topProducts, monthlyTrend, statusBreakdown } = analyticsData;

  // Chart data for monthly spending trend
  const spendingTrendData = {
    labels: monthlyTrend.map(m => m.month),
    datasets: [{
      label: 'Spending',
      data: monthlyTrend.map(m => m.spent),
      borderColor: '#0D7463',
      backgroundColor: 'rgba(13, 116, 99, 0.1)',
      tension: 0.4,
      fill: true,
      borderWidth: 3,
      pointRadius: 4,
      pointBackgroundColor: '#0D7463',
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      pointHoverRadius: 6
    }]
  };

  // Chart data for category breakdown
  const categoryData = {
    labels: topCategories.map(c => c.category),
    datasets: [{
      label: 'Spending by Category',
      data: topCategories.map(c => c.totalSpent),
      backgroundColor: [
        'rgba(13, 116, 99, 0.8)',
        'rgba(55, 185, 165, 0.8)',
        'rgba(198, 229, 218, 0.8)',
        'rgba(10, 90, 77, 0.8)',
        'rgba(31, 184, 163, 0.8)'
      ],
      borderColor: [
        '#0D7463',
        '#37B9A5',
        '#C6E5DA',
        '#0A5A4D',
        '#1FB8A3'
      ],
      borderWidth: 2,
      borderRadius: 8
    }]
  };

  // Chart data for order status
  const statusData = {
    labels: Object.keys(statusBreakdown),
    datasets: [{
      label: 'Orders by Status',
      data: Object.values(statusBreakdown),
      backgroundColor: [
        'rgba(13, 116, 99, 0.8)',
        'rgba(55, 185, 165, 0.8)',
        'rgba(10, 90, 77, 0.8)',
        'rgba(198, 229, 218, 0.8)',
        'rgba(31, 184, 163, 0.8)',
        'rgba(78, 205, 186, 0.8)'
      ],
      borderColor: '#fff',
      borderWidth: 3,
      hoverOffset: 8
    }]
  };

  return (
    <div className="customer-analytics">
      <div className="analytics-header">
        <div className="header-icon-wrapper">
          <BarChart3 className="header-icon" />
        </div>
        <div className="header-content">
          <h1>My Analytics Dashboard</h1>
          <p className="customer-info">
            {customer.name} | Member since {formatDate(customer.memberSince)}
          </p>
        </div>
        <div className="header-actions">
          <select value={period} onChange={(e) => setPeriod(e.target.value)} className="period-selector">
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="week">This Week</option>
            <option value="last7days">Last 7 Days</option>
            <option value="month">This Month</option>
            <option value="last30days">Last 30 Days</option>
            <option value="last90days">Last 90 Days</option>
            <option value="previousMonth">Previous Month</option>
            <option value="year">This Year</option>
            <option value="previousYear">Previous Year</option>
            <option value="custom">Custom Range</option>
          </select>

          {period === 'custom' && (
            <div className="custom-date-range">
              <input
                type="date"
                value={customDates.startDate}
                onChange={(e) => setCustomDates({ ...customDates, startDate: e.target.value })}
                className="date-input"
              />
              <span>to</span>
              <input
                type="date"
                value={customDates.endDate}
                onChange={(e) => setCustomDates({ ...customDates, endDate: e.target.value })}
                className="date-input"
              />
            </div>
          )}

          <div className="export-buttons">
            <button onClick={() => handleExport('csv')} className="btn-export">
              <Download className="btn-icon" /> Export CSV
            </button>
            <button onClick={() => handleExport('json')} className="btn-export">
              <Download className="btn-icon" /> Export JSON
            </button>
          </div>
        </div>
      </div>

      <div className="analytics-tabs">
        <button
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`tab ${activeTab === 'spending' ? 'active' : ''}`}
          onClick={() => setActiveTab('spending')}
        >
          Spending Analysis
        </button>
        <button
          className={`tab ${activeTab === 'products' ? 'active' : ''}`}
          onClick={() => setActiveTab('products')}
        >
          Products
        </button>
        <button
          className={`tab ${activeTab === 'orders' ? 'active' : ''}`}
          onClick={() => setActiveTab('orders')}
        >
          Order History
        </button>
      </div>

      <div className="analytics-content">
        {activeTab === 'overview' && (
          <div className="overview-tab">
            <div className="summary-cards">
              <div className="summary-card card-orders">
                <div className="card-icon-wrapper">
                  <ShoppingBag className="card-icon" />
                </div>
                <div className="card-content">
                  <h3>Total Orders</h3>
                  <p className="card-value">{summary.totalOrders}</p>
                </div>
              </div>

              <div className="summary-card card-spent">
                <div className="card-icon-wrapper">
                  <DollarSign className="card-icon" />
                </div>
                <div className="card-content">
                  <h3>Total Spent</h3>
                  <p className="card-value">{formatCurrency(summary.totalSpent)}</p>
                </div>
              </div>

              <div className="summary-card card-average">
                <div className="card-icon-wrapper">
                  <TrendingUp className="card-icon" />
                </div>
                <div className="card-content">
                  <h3>Average Order</h3>
                  <p className="card-value">{formatCurrency(summary.averageOrderValue)}</p>
                </div>
              </div>

              <div className="summary-card card-savings">
                <div className="card-icon-wrapper">
                  <Gift className="card-icon" />
                </div>
                <div className="card-content">
                  <h3>Total Savings</h3>
                  <p className="card-value">{formatCurrency(summary.totalSavings)}</p>
                </div>
              </div>
            </div>

            <div className="charts-grid">
              <div className="chart-container">
                <h3>Spending Trend (6 Months)</h3>
                <Line data={spendingTrendData} options={{ responsive: true, maintainAspectRatio: true }} />
              </div>

              <div className="chart-container">
                <h3>Order Status Breakdown</h3>
                <Pie data={statusData} options={{ responsive: true, maintainAspectRatio: true }} />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'spending' && (
          <div className="spending-tab">
            <div className="charts-grid">
              <div className="chart-container large">
                <h3>Top Categories by Spending</h3>
                {topCategories && topCategories.length > 0 ? (
                  <Bar
                    data={categoryData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          backgroundColor: 'rgba(13, 116, 99, 0.95)',
                          titleColor: '#fff',
                          bodyColor: '#fff',
                          padding: 12,
                          borderColor: '#37B9A5',
                          borderWidth: 1,
                          callbacks: {
                            label: function(context) {
                              return `Spent: ${formatCurrency(context.parsed.y)}`;
                            }
                          }
                        }
                      },
                      scales: {
                        x: {
                          grid: {
                            display: false
                          },
                          ticks: {
                            color: '#374151',
                            font: {
                              size: 12,
                              weight: 600
                            }
                          }
                        },
                        y: {
                          beginAtZero: true,
                          grid: {
                            color: 'rgba(13, 116, 99, 0.1)'
                          },
                          ticks: {
                            color: '#374151',
                            font: {
                              size: 12,
                              weight: 500
                            },
                            callback: function(value) {
                              return formatCurrency(value);
                            }
                          }
                        }
                      }
                    }}
                  />
                ) : (
                  <div className="empty-state">
                    <BarChart3 size={64} className="empty-icon" />
                    <h3>No Category Data</h3>
                    <p>Start shopping to see your spending by category</p>
                  </div>
                )}
              </div>
            </div>

            <div className="data-tables">
              <div className="data-table-container">
                <h3>Top Merchants</h3>
                {topMerchants && topMerchants.length > 0 ? (
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>Merchant</th>
                        <th>Orders</th>
                        <th>Total Spent</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topMerchants.map((merchant, idx) => (
                        <tr key={idx}>
                          <td>{merchant.name}</td>
                          <td>{merchant.orderCount}</td>
                          <td><strong>{formatCurrency(merchant.totalSpent)}</strong></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>No merchant data available</p>
                )}
              </div>

              <div className="data-table-container">
                <h3>Top Categories</h3>
                {topCategories && topCategories.length > 0 ? (
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Items</th>
                        <th>Total Spent</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topCategories.map((category, idx) => (
                        <tr key={idx}>
                          <td><strong>{category.category}</strong></td>
                          <td>{category.itemCount}</td>
                          <td><strong>{formatCurrency(category.totalSpent)}</strong></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>No category data available</p>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'products' && (
          <div className="products-tab">
            <h3>Top Products by Spending</h3>
            {topProducts && topProducts.length > 0 ? (
              <div className="products-grid">
                {topProducts.map((product, idx) => (
                  <div key={idx} className="product-card">
                    {product.image ? (
                      <img src={product.image} alt={product.name} className="product-image" />
                    ) : (
                      <div className="product-image product-image-placeholder">
                        <Package size={48} color="#9ca3af" />
                      </div>
                    )}
                    <div className="product-details">
                      <h4>{product.name}</h4>
                      <p className="product-category">{product.category || 'Uncategorized'}</p>
                      {product.merchant && (
                        <p className="product-merchant">by {product.merchant.username}</p>
                      )}
                      <div className="product-pricing">
                        <div className="price-row">
                          <span className="price-label">Current Price:</span>
                          <span className="price-value">{formatCurrency(product.currentPrice)}</span>
                        </div>
                        <div className="price-row">
                          <span className="price-label">Avg. Paid:</span>
                          <span className="price-value">{formatCurrency(product.averagePrice)}</span>
                        </div>
                      </div>
                      <div className="product-stats">
                        <div className="stat-item">
                          <span className="stat-label">Quantity</span>
                          <span className="stat-value">{product.quantity}</span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-label">Orders</span>
                          <span className="stat-value">{product.orderCount}</span>
                        </div>
                      </div>
                      <div className="product-total-spent">
                        <span className="total-label">Total Spent</span>
                        <span className="total-value">{formatCurrency(product.totalSpent)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <Package size={64} className="empty-icon" />
                <h3>No Products Found</h3>
                <p>You haven't purchased any products during this period</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'orders' && (
          <div className="orders-tab">
            <h3 className="canalytics-section-title">Order history</h3>
            {orderHistory ? (
              orderHistory.orders && orderHistory.orders.length > 0 ? (
                <>
                  <ul className="canalytics-orders">
                    {orderHistory.orders.map((order) => {
                      const itemCount = order.items?.length || order.itemCount || 0;
                      const status = (order.status || 'pending').toLowerCase();
                      return (
                        <li key={order.id} className="canalytics-order">
                          <div className="canalytics-order__head">
                            <div className="canalytics-order__id">
                              #{order.orderNumber || order.id?.substring(0, 8)}
                            </div>
                            <span className={`canalytics-pill canalytics-pill--${status}`}>
                              {order.status || 'Pending'}
                            </span>
                          </div>
                          <div className="canalytics-order__meta">
                            {order.storeName && (
                              <span className="canalytics-order__chip">
                                <Store size={12} aria-hidden />
                                {order.storeName}
                              </span>
                            )}
                            <span className="canalytics-order__chip">
                              <Calendar size={12} aria-hidden />
                              {formatDate(order.createdAt)}
                            </span>
                            {itemCount > 0 && (
                              <span className="canalytics-order__chip">
                                <Package size={12} aria-hidden />
                                {itemCount} {itemCount === 1 ? 'item' : 'items'}
                              </span>
                            )}
                          </div>
                          {order.items && order.items.length > 0 && (
                            <ul className="canalytics-order__items">
                              {order.items.slice(0, 3).map((item, idx) => (
                                <li key={idx} className="canalytics-order__item">
                                  <div className="canalytics-order__thumb">
                                    {item.productImage
                                      ? <img src={item.productImage} alt={item.productName || 'Product'} />
                                      : <Package size={16} aria-hidden />}
                                  </div>
                                  <div className="canalytics-order__item-body">
                                    <div className="canalytics-order__item-name">
                                      {item.productName || 'Unknown product'}
                                    </div>
                                    <div className="canalytics-order__item-qty">Qty {item.quantity || 1}</div>
                                  </div>
                                </li>
                              ))}
                              {order.items.length > 3 && (
                                <li className="canalytics-order__more">
                                  +{order.items.length - 3} more
                                </li>
                              )}
                            </ul>
                          )}
                          <div className="canalytics-order__total">
                            <span>Total</span>
                            <strong>{formatCurrency(order.total)}</strong>
                          </div>
                        </li>
                      );
                    })}
                  </ul>

                  {orderHistory.pagination.pages > 1 && (
                    <div className="pagination">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="btn-page"
                      >
                        Previous
                      </button>
                      <span className="page-info">
                        Page {orderHistory.pagination.page} of {orderHistory.pagination.pages}
                      </span>
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(orderHistory.pagination.pages, prev + 1))}
                        disabled={currentPage === orderHistory.pagination.pages}
                        className="btn-page"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="empty-state">
                  <ShoppingBag size={48} className="empty-icon" />
                  <h3>No orders found</h3>
                  <p>You don't have any orders during this period.</p>
                </div>
              )
            ) : (
              <div className="analytics-loading">
                <div className="spinner"></div>
                <p>Loading order history...</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerAnalytics;
